import { fragmentAddress } from "@saleor/fragments/address";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeQuery from "@saleor/hooks/makeQuery";
import makeTopLevelSearch from "@saleor/hooks/makeTopLevelSearch";
import gql from "graphql-tag";

import { TypedQuery } from "../queries";
import { RunnerList, RunnerListVariables } from "./types/RunnerList";
import { SearchRunner, SearchRunnerVariables } from "./types/SearchRunner";
import {
  StoreOrderDetails,
  StoreOrderDetailsVariables
} from "./types/StoreOrderDetails";
import {
  StoreOrderList,
  StoreOrderListVariables
} from "./types/StoreOrderList";
import {
  StoreOrderProductList,
  StoreOrderProductListVariables
} from "./types/StoreOrderProductList";

export const storeOrderListQuery = gql`
  ${fragmentAddress}
  query StoreOrderList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: StoreOrderFilterInput
    $sort: StoreOrderSortingInput
  ) {
    storeOrders(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
      sortBy: $sort
    ) {
      edges {
        node {
          id
          smopStatus
          tenant {
            id
            storeName
            brand {
              id
              name
              legalName
            }
          }
          orderCode
          runner {
            id
            email
            firstName
            lastName
            contactNo
          }
          order {
            id
            billingAddress {
              ...AddressFragment
            }
            shippingAddress {
              id
              streetAddress1
              streetAddress2
              city
              cityArea
              postalCode
            }
            shippingMethod {
              id
              name
              deliveryType
            }
            payments {
              id
              paymentMethodType
              channelCode
            }
            user {
              id
              firstName
              lastName
              email
              contactNo
            }
            userEmail
            shippingSchedule
            created
            paymentStatus
            status
            number
            total {
              __typename
              gross {
                __typename
                amount
                currency
              }
            }
            targetTime
            mall {
              id
              name
            }
          }
          totalAmount
          subtotal
          discountAmount
        }
      }
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
`;
export const useStoreOrderListQuery = makeQuery<
  StoreOrderList,
  StoreOrderListVariables
>(storeOrderListQuery);

export const storeOrderDetailsQuery = gql`
  query StoreOrderDetails($id: ID!) {
    storeOrder(id: $id) {
      id
      parcelImageUrl
      receiptImageUrl
      parcelNo
      smopStatus
      dropBinCode
      tenant {
        id
        legalName
        storeName
        brand {
          id
          name
          logo {
            url
            alt
          }
        }
      }
      events {
        emailType
        id
        date
        type
        message
        user {
          id
          email
        }
        invoiceNumber
        quantity
      }
      orderCode
      runner {
        id
        email
        firstName
        lastName
        contactNo
      }
      order {
        id
        verticalType
        distanceFromMall
        mall {
          id
          name
          code
        }
        subOrders {
          id
          orderCode
          totalAmount
        }
        discount {
          currency
          amount
        }
        discountName
        rider {
          firstName
          lastName
          email
          contactNo
        }
        shippingPrice {
          gross {
            currency
            amount
          }
        }
        shippingMethod {
          id
          name
          deliveryType
        }
        shippingAddress {
          id
          firstName
          lastName
          companyName
          streetAddress1
          streetAddress2
          city
          cityArea
          postalCode
          country {
            code
          }
          longitude
          latitude
          unitDetails
          streetBrgy
          landmark
          provinceName
          provinceCode
        }
        payments {
          id
          paymentMethodType
          chargeStatus
          channelCode
        }
        user {
          id
          contactNo
          email
          firstName
          lastName
          smac
        }
        userEmail
        orderCode
        shippingSchedule
        smopStatus
        created
        paymentStatus
        status
        number
        total {
          gross {
            amount
          }
        }
        voucher {
          id
          name
          type
          code
          discountValueType
          discountValue
        }
        isMultiTenant
        hasCutlery
        images {
          id
          sortOrder
          alt
          url
        }
        dropOffLocation {
          id
          name
          code
          mall {
            id
            name
          }
        }
        bin {
          id
          rack {
            id
          }
          binNo
          isAvailable
          isVisible
        }
        binCode
      }
      lines {
        id
        status
        productName
        variantName
        quantity
        unitPrice {
          gross {
            amount
          }
        }
        totalPrice {
          currency
          gross {
            currency
            amount
          }
        }
        notes
        variant {
          id
          name
          sku
        }
      }
      totalAmount
      discountAmount
      subtotal
      smac
      mall {
        id
        name
      }
    }
  }
`;
export const TypedOrderDetailsQuery = TypedQuery<
  StoreOrderDetails,
  StoreOrderDetailsVariables
>(storeOrderDetailsQuery);

export const useOrderDetailsQuery = makeQuery<
  StoreOrderDetails,
  StoreOrderDetailsVariables
>(storeOrderDetailsQuery);

const runnerList = gql`
  query RunnerList($first: Int, $filter: StaffUserInput) {
    staffUsers(first: $first, filter: $filter) {
      edges {
        cursor
        node {
          id
          email
          firstName
          lastName
          contactNo
          accessGroup {
            name
            smopUsertypeCode
            accesses {
              objectId
              accessType
              description
            }
          }
          status
        }
      }
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
`;
export const TypedRunnerListQuery = TypedQuery<RunnerList, RunnerListVariables>(
  runnerList
);

export const searchRunner = gql`
  ${pageInfoFragment}
  query SearchRunner(
    $after: String
    $first: Int!
    $query: String!
    $status: LockStatus
    $userType: [SmopUserTypeCode]
    $malls: [ID]
  ) {
    search: staffUsers(
      after: $after
      first: $first
      filter: {
        search: $query
        smopUserType: $userType
        status: $status
        malls: $malls
      }
    ) {
      edges {
        node {
          id
          email
          firstName
          lastName
          isActive
          status
          avatar {
            alt
            url
          }
          accessGroup {
            name
            smopUsertypeCode
            accesses {
              objectId
              accessType
              description
            }
          }
          contactNo
          dateJoined
          status
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

export const useSearchRunner = makeTopLevelSearch<
  SearchRunner,
  SearchRunnerVariables
>(searchRunner);

const productList = gql`
  ${pageInfoFragment}
  query StoreOrderProductList($first: Int!, $query: String!) {
    search: products(first: $first, filter: { searchByName: $query }) {
      edges {
        node {
          id
          name
          variants {
            id
            name
            price {
              currency
              amount
            }
          }

          productVisibility {
            isVisible
            isAvailable
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

export const useProductList = makeTopLevelSearch<
  StoreOrderProductList,
  StoreOrderProductListVariables
>(productList);
